import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { fastSearch } from "../utils/fastSearch";

const categories = [
  "mandir",
  "hospital",
  "restaurant",
  "waterpark",
  "mall",
  "cafe",
];

const SearchPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const city = params.get("city");
  const initialQuery = params.get("q") || "";

  const [query, setQuery] = React.useState(initialQuery);
  const [activeCategory, setActiveCategory] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [results, setResults] = React.useState([]);
  const [showSuggest, setShowSuggest] = React.useState(false);

  const runSearch = React.useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      const data = fastSearch({
        city,
        query,
        category: activeCategory,
      });
      setResults(data);
      setLoading(false);
    }, 200);
  }, [city, query, activeCategory]);

  React.useEffect(() => {
    runSearch();
  }, [runSearch]);

  const suggestions = query ? results.slice(0, 5) : [];

  const categoryParam = activeCategory;
  const hasQuery = Boolean(query);

  const isIndexable = city && categoryParam && !hasQuery;

  const seoTitle = isIndexable
    ? `Best ${categoryParam} in ${city} | NearbyGuide`
    : `Search places in ${city} | NearbyGuide`;

  const seoDescription = isIndexable
    ? `Discover top-rated ${categoryParam} in ${city}. Find locations, details, timings and nearby places with NearbyGuide.`
    : `Search nearby places in ${city}. Explore temples, hospitals, restaurants, malls and more.`;

  const canonicalUrl = isIndexable
    ? `https://nearbyguide.in/search?city=${city}&category=${categoryParam}`
    : `https://nearbyguide.in/search?city=${city}`;

  return (
    <Box sx={{ minHeight: "100vh", background: "#F7F9FC", py: 6 }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        noIndex={!isIndexable}
      >
        {/* SCHEMA ONLY FOR INDEXABLE PAGES */}
        {isIndexable && results.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: `Best ${categoryParam} in ${city}`,
              itemListElement: results.slice(0, 10).map((item, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: item.name,
                url: `https://nearbyguide.in/city/${item.city}/${item.category}/${item.slug}`,
              })),
            })}
          </script>
        )}
      </SEO>
      <Container maxWidth="lg">
        {/* SEARCH BAR */}
        <Box
          sx={{
            background: "#fff",
            borderRadius: 3,
            p: 1.2,
            boxShadow: "0 22px 60px rgba(13,59,102,0.12)",
            position: "relative",
          }}
        >
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowSuggest(true);
              }}
              placeholder="Search places, areas or categories"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "#6B8BAA" }} />,
              }}
              sx={{
                "& fieldset": { border: "none" },
                "& input": { py: 1.2, fontSize: "1rem" },
              }}
            />

            <Button
              onClick={runSearch}
              sx={{
                px: 4,
                fontWeight: 700,
                borderRadius: 2,
                background: "linear-gradient(135deg,#0D3B66,#1E6091)",
              }}
              variant="contained"
            >
              Search
            </Button>
          </Stack>

          {/* AUTO SUGGEST */}
          {showSuggest && suggestions.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                background: "#fff",
                mt: 1,
                borderRadius: 2,
                boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
                zIndex: 10,
              }}
            >
              {suggestions.map((s, i) => (
                <Box
                  key={i}
                  onClick={() => {
                    setQuery(s.name);
                    setShowSuggest(false);
                    runSearch();
                  }}
                  sx={{
                    px: 2,
                    py: 1.2,
                    cursor: "pointer",
                    "&:hover": { background: "#F1F6FA" },
                  }}
                >
                  <Typography fontWeight={600}>{s.name}</Typography>
                  <Typography fontSize="0.8rem" color="text.secondary">
                    {s.area}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* CATEGORY FILTER */}
        <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: "wrap" }}>
          {categories.map((cat) => {
            const active = activeCategory === cat;
            return (
              <Chip
                key={cat}
                label={cat}
                clickable
                onClick={() => setActiveCategory(active ? null : cat)}
                sx={{
                  px: 2,
                  py: 0.8,
                  fontWeight: 600,
                  borderRadius: 999,
                  background: active ? "#0D3B66" : "#EEF4F8",
                  color: active ? "#fff" : "#0D3B66",
                  boxShadow: active
                    ? "0 10px 26px rgba(13,59,102,0.35)"
                    : "none",
                  textTransform: "capitalize",
                }}
              />
            );
          })}
        </Stack>

        {/* LOADING */}
        {loading && (
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1.5 }} color="text.secondary">
              Finding best places near you…
            </Typography>
          </Box>
        )}

        {/* RESULTS */}
        {!loading && results.length > 0 && (
          <Stack spacing={2.5} sx={{ mt: 4 }}>
            {results.map((item, i) => (
              <Card
                key={i}
                onClick={() =>
                  navigate(`/city/${item.city}/${item.category}/${item.slug}`)
                }
                sx={{
                  p: 2.4,
                  borderRadius: 2,
                  cursor: "pointer",
                  boxShadow: "0 18px 40px rgba(0,0,0,0.08)",
                  transition: "all .25s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 28px 60px rgba(0,0,0,0.14)",
                  },
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box>
                    <Typography fontWeight={800} fontSize="1.15rem">
                      {item.name}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Chip size="small" label={item.category} />
                      {item.area && (
                        <Chip
                          size="small"
                          icon={<LocationOnIcon />}
                          label={item.area}
                        />
                      )}
                    </Stack>

                    <Typography sx={{ mt: 1.2 }} color="text.secondary">
                      {item.details}
                    </Typography>
                  </Box>

                  <ArrowForwardIcon sx={{ color: "#0D3B66" }} />
                </Stack>
              </Card>
            ))}
          </Stack>
        )}

        {/* EMPTY STATE */}
        {!loading && results.length === 0 && (
          <Box sx={{ textAlign: "center", mt: 8 }}>
            <Typography fontSize="1.4rem" fontWeight={800}>
              No places found 😕
            </Typography>
            <Typography sx={{ mt: 1 }} color="text.secondary">
              Try another name, area, or category like <b>hospital</b>,{" "}
              <b>mandir</b>, <b>restaurant</b>
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SearchPage;
